import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Lock, Crown, Coins, ArrowRight, LogIn, Sparkles } from 'lucide-react';
import { API } from '../config/api';



/**
 * Stream Access Gate
 * Shows upgrade prompt when user doesn't have required tier access
 */
export function StreamAccessGate({ 
  streamId,
  accessCheck,
  onClose,
  isAuthenticated = false
}) {
  const navigate = useNavigate();
  
  if (!accessCheck || accessCheck.has_access) return null;

  const progress = accessCheck.required_coins > 0 
    ? Math.min(100, (accessCheck.user_coins_spent / accessCheck.required_coins) * 100)
    : 0;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-background border-amber-500/30 max-w-md" data-testid="access-gate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Lock className="w-5 h-5" />
            Exclusive Stream
          </DialogTitle>
          <DialogDescription>
            This stream is for {accessCheck.required_tier_name} listeners
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tier requirement badge */}
          <div className="flex justify-center">
            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black px-4 py-2 text-sm">
              <Crown className="w-4 h-4 mr-2" />
              {accessCheck.required_tier_name} Required
            </Badge>
          </div>

          {/* Message */}
          <p className="text-center text-muted-foreground text-sm">
            {accessCheck.message}
          </p>

          {/* Progress (for logged-in users) */}
          {isAuthenticated && accessCheck.required_coins > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Your progress</span>
                <span className="text-amber-400">
                  {accessCheck.user_coins_spent.toLocaleString()} / {accessCheck.required_coins.toLocaleString()} coins
                </span>
              </div>
              <Progress value={progress} className="h-2" />
              {accessCheck.coins_needed > 0 && (
                <p className="text-center text-xs text-muted-foreground">
                  Spend <span className="text-amber-400 font-medium">{accessCheck.coins_needed.toLocaleString()}</span> more coins to unlock!
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            {!isAuthenticated ? (
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In to Check Access
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/live')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Coins className="w-4 h-4 mr-2" />
                Support Artists to Level Up
              </Button>
            )}
            
            <Button variant="ghost" onClick={onClose}>
              Watch Other Streams
            </Button>
          </div>

          {/* Tier info */}
          <div className="border-t border-border pt-4">
            <p className="text-xs text-center text-muted-foreground">
              <Sparkles className="w-3 h-3 inline mr-1 text-amber-400" />
              Listener tiers are earned by supporting artists with coins, gifts, and tips
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline access check banner for stream pages
 */
export function StreamAccessBanner({ accessCheck }) {
  if (!accessCheck || accessCheck.has_access) return null;

  return (
    <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-4 mb-4" data-testid="access-banner">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
          <Lock className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-amber-400">{accessCheck.required_tier_name} Access Required</p>
          <p className="text-xs text-muted-foreground">{accessCheck.message}</p>
        </div>
        <Badge className="bg-amber-500 text-black">
          {accessCheck.coins_needed.toLocaleString()} coins to go
        </Badge>
      </div>
    </div>
  );
}

export default StreamAccessGate;
